// Piezas de documentos (PP-23) que comparten la ficha del vehiculo o
// conductor y la lista de Vencimientos: badge de estado, formulario de carga o
// renovacion y el copy de cada error. El copy vive aca y no en la logica.
import { useState } from "react";
import type { DocumentStatus } from "@ucorp/contracts";
import { Alert } from "../../components/ui/alert.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { DOCUMENT_STATUS_LABEL, documentTypeLabel, type UploadDraft } from "../../logic/documents.js";
import { Field } from "../parts.js";
import { FleetError, type FleetErrorKind } from "../port.js";

/** Estado de una fila: lo vencido y lo que falta impiden operar; lo por vencer avisa. */
export function DocumentStatusBadge({ status }: { status: DocumentStatus | "missing" }): React.JSX.Element {
  if (status === "missing") return <Badge variant="critico">Falta</Badge>;
  if (status === "expired") return <Badge variant="critico">{DOCUMENT_STATUS_LABEL.expired}</Badge>;
  if (status === "expiring_soon") return <Badge variant="ambar">{DOCUMENT_STATUS_LABEL.expiring_soon}</Badge>;
  return <Badge variant="ok">{DOCUMENT_STATUS_LABEL[status]}</Badge>;
}

const DOCUMENT_ERROR_COPY: Record<FleetErrorKind, string> = {
  access: "Ese vehículo o conductor no es de la empresa: no se pueden cargar ni renovar sus documentos.",
  not_found: "No se encontró el vehículo, el conductor o el documento. Puede haber cambiado: actualizar la lista.",
  // 409 document_exists (ADR-014 §2): otro operador acaba de cargar ese tipo.
  conflict: "Ya hay un documento de ese tipo cargado, quizás por otra persona recién. La lista se actualizó: usar Renovar.",
  subcontract_missing: "No se pudo guardar el documento. Reintentar en unos minutos.",
  validation: "Hay datos que no se pueden guardar. Revisar el tipo y la fecha de vencimiento.",
  unavailable: "No se pudo guardar el documento. Reintentar en unos minutos.",
};

export function documentErrorCopy(error: unknown): string {
  return DOCUMENT_ERROR_COPY[error instanceof FleetError ? error.kind : "unavailable"];
}

interface FormProps {
  /** "create": alta de un tipo que falta; "renew": reemplaza al vigente. */
  mode: "create" | "renew";
  /** Tipo fijo (fila de la ficha o de Vencimientos); undefined: se escribe. */
  type: string | undefined;
  /** De quien es el papel, para el titulo accesible del formulario. */
  subjectLabel: string;
  /** Devuelve el mensaje de error a mostrar, o undefined si se guardo. */
  onSubmit: (draft: UploadDraft) => Promise<string | undefined>;
  onCancel: () => void;
}

/**
 * Formulario en linea de carga o renovacion. El archivo no viaja: la API
 * guarda tipo, vencimiento y referencia, sin el archivo en si. La referencia
 * arranca vacia tambien al renovar: la del papel anterior no es la del nuevo.
 */
export function DocumentForm({ mode, type, subjectLabel, onSubmit, onCancel }: FormProps): React.JSX.Element {
  const [draft, setDraft] = useState<UploadDraft>({ type: type ?? "", expiresAt: "", reference: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const verb = mode === "renew" ? "Renovar" : "Cargar";
  const title = type === undefined ? `Cargar documento de ${subjectLabel}` : `${verb} ${documentTypeLabel(type)} de ${subjectLabel}`;

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    onSubmit(draft)
      .then((message) => {
        if (message !== undefined) setError(message);
      })
      .finally(() => setBusy(false));
  }

  return (
    <form aria-label={title} onSubmit={submit} className="flex flex-col gap-3 rounded-md border border-linea bg-hundida p-4">
      <p className="m-0 text-sm font-semibold text-texto">{title}</p>
      {mode === "renew" && (
        <p className="m-0 text-xs text-texto-suave">La renovación reemplaza al documento vigente: no se crea otro.</p>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        {type === undefined && (
          <Field
            label="Tipo de documento"
            hint="Por ejemplo: seguro, vtv, licencia. Si ya tiene uno de ese tipo, se renueva."
            value={draft.type}
            required
            onChange={(event) => setDraft({ ...draft, type: event.target.value })}
          />
        )}
        <Field
          label="Vencimiento"
          type="date"
          hint="Vacío si el documento no vence."
          value={draft.expiresAt}
          onChange={(event) => setDraft({ ...draft, expiresAt: event.target.value })}
        />
        <Field
          label="Referencia"
          hint="Número de póliza, de oblea o de licencia. Opcional."
          value={draft.reference}
          onChange={(event) => setDraft({ ...draft, reference: event.target.value })}
        />
      </div>
      {error !== undefined && <Alert variant="error">{error}</Alert>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? "Guardando..." : verb}
        </Button>
        <Button type="button" size="sm" variant="secundario" disabled={busy} onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
